import { VisibilityOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, TextField, Typography } from '@mui/material';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { object, string } from 'yup';

import {
    createCustomList,
    getCustomLists,
    renameCustomList,
} from 'entities/CustomList';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import { getRouteCatalogList } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { Loader } from 'shared/ui/Loader/Loader';

import { CustomListFormMode } from '../../model/types/customListFormTypes';

const NEW_LIST_NAME_HINT = 'customLists.nameHint';

interface CustomListFormProps {
    mode: CustomListFormMode;
    listId?: string;
    initialName?: string;
    propertyId: string;
    onClose: () => void;
}

interface CustomListFormValues {
    name: string;
}

const CUSTOM_LIST_FORM_ID = 'custom-list-form';

const normalizeName = (value: string | undefined) =>
    (value ?? '').trim().toLowerCase();

export const CustomListForm = memo(
    ({
        mode,
        listId,
        initialName = '',
        propertyId,
        onClose,
    }: CustomListFormProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const navigate = useNavigate();
        const property = useSelector(getSelectedProperty);
        const lists = useSelector(getCustomLists);
        const [loading, setLoading] = useState(false);
        const [submitClicked, setSubmitClicked] = useState(false);
        const nameInputRef = useRef<HTMLInputElement>(null);

        const propertyName =
            property?.propertyNameLegal ||
            property?.propertyNameMarketing ||
            '';

        const validationSchema = useMemo(() => {
            const taken = new Set<string>();
            lists.forEach((list) => {
                if (
                    mode === CustomListFormMode.RENAME &&
                    listId &&
                    list.id === listId
                ) {
                    return;
                }
                taken.add(normalizeName(list.name));
            });

            return object({
                name: string()
                    .trim()
                    .required(t('Required'))
                    .test('unique-list-name', function (value) {
                        const normalized = normalizeName(value);
                        if (!normalized) {
                            return true;
                        }
                        if (taken.has(normalized)) {
                            return this.createError({
                                message: t('customLists.duplicateName'),
                            });
                        }
                        return true;
                    }),
            });
        }, [listId, mode, lists, t]);

        const handleSubmit = useCallback(
            async (
                values: CustomListFormValues,
                helpers: FormikHelpers<CustomListFormValues>,
            ) => {
                if (loading) {
                    return;
                }
                setLoading(true);

                const name = values.name.trim();
                const response =
                    mode === CustomListFormMode.CREATE
                        ? await dispatch(createCustomList({ propertyId, name }))
                        : await dispatch(
                              renameCustomList({
                                  listId: listId as string,
                                  name,
                              }),
                          );

                if (response.meta.requestStatus === 'fulfilled') {
                    if (mode === CustomListFormMode.CREATE) {
                        const detail = response.payload as {
                            id: string;
                            name: string;
                        };
                        toast(
                            t('customLists.listCreated', {
                                name: detail.name,
                            }),
                        );
                        onClose();
                        navigate(getRouteCatalogList(propertyId, detail.id));
                    } else {
                        const detail = response.payload as { name: string };
                        toast(
                            t('customLists.listRenamed', {
                                name: detail.name,
                            }),
                        );
                        onClose();
                    }
                } else {
                    const payload = response.payload;
                    const message = Array.isArray(payload)
                        ? payload[0]
                        : typeof payload === 'string'
                        ? payload
                        : t('Something went wrong');
                    helpers.setErrors({ name: message });
                }

                setLoading(false);
            },
            [dispatch, listId, loading, mode, navigate, onClose, propertyId, t],
        );

        const formTitle =
            mode === CustomListFormMode.CREATE
                ? t('customLists.newList')
                : t('customLists.renameList');

        useEffect(() => {
            if (mode !== CustomListFormMode.RENAME) {
                return;
            }

            nameInputRef.current?.select();
        }, [mode]);

        return (
            <Dialog
                open
                onClose={onClose}
                sx={{ '& .MuiDialog-paper': { overflow: 'unset' } }}
            >
                <Formik
                    initialValues={{ name: initialName }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnBlur={false}
                    enableReinitialize
                >
                    {(props: FormikProps<CustomListFormValues>) => (
                        <DialogBoxContent
                            onClose={onClose}
                            buttons={
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    width="100%"
                                    gap="8px"
                                >
                                    <Button
                                        type="button"
                                        onClick={onClose}
                                        data-testclass="custom-list-form-cancel"
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        form={CUSTOM_LIST_FORM_ID}
                                        variant="contained"
                                        disabled={
                                            loading ||
                                            (mode ===
                                                CustomListFormMode.RENAME &&
                                                !props.dirty)
                                        }
                                        startIcon={
                                            loading ? (
                                                <Loader color="inherit" />
                                            ) : undefined
                                        }
                                        data-testclass="custom-list-form-submit"
                                    >
                                        {mode === CustomListFormMode.CREATE
                                            ? t('Add items')
                                            : t('Rename')}
                                    </Button>
                                </Box>
                            }
                        >
                            <Box
                                component="form"
                                id={CUSTOM_LIST_FORM_ID}
                                noValidate
                                onSubmit={(event) => {
                                    setSubmitClicked(true);
                                    props.handleSubmit(event);
                                }}
                                sx={{
                                    maxWidth: '400px',
                                    paddingBottom: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                    }}
                                >
                                    {propertyName ? (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'rgba(0, 0, 0, 0.4)',
                                            }}
                                        >
                                            {propertyName}
                                        </Typography>
                                    ) : null}
                                    <Typography variant="h4">
                                        {formTitle}
                                    </Typography>
                                </Box>
                                <TextField
                                    name="name"
                                    label={t('customLists.listName')}
                                    value={props.values.name}
                                    onChange={props.handleChange}
                                    error={
                                        submitClicked &&
                                        Boolean(props.errors.name)
                                    }
                                    helperText={
                                        submitClicked
                                            ? props.errors.name
                                            : undefined
                                    }
                                    inputRef={nameInputRef}
                                    fullWidth
                                    autoFocus
                                    data-testclass="custom-list-form-name"
                                />
                                {mode === CustomListFormMode.CREATE ? (
                                    <>
                                        <Typography variant="body1">
                                            {t(NEW_LIST_NAME_HINT)}
                                        </Typography>
                                        {propertyName ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                }}
                                            >
                                                <VisibilityOutlined
                                                    sx={{
                                                        width: '16px',
                                                        height: '16px',
                                                    }}
                                                />
                                                <Typography variant="body1">
                                                    {t(
                                                        'customLists.visibleToTeam',
                                                        { propertyName },
                                                    )}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </>
                                ) : null}
                            </Box>
                        </DialogBoxContent>
                    )}
                </Formik>
            </Dialog>
        );
    },
);
